import React, { useState, useEffect, useRef } from 'react';
import _get from 'lodash/get';
import { differenceInYears } from 'date-fns';
import { client } from 'cccisd-apollo';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import IconFamily from 'cccisd-icons/users4';
import IconQuestion from 'cccisd-icons/question4';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Tabs from 'cccisd-tabs';
import Tooltip from 'cccisd-tooltip';
import { TFH_DEPLOYMENT_HANDLE } from '../../TfhProgram';
import { getSocioenvironmentalDeploymentId, isSupervisorViewing } from '../../../helpers/helpers.js';
import reportTemplates from 'js/vendor/reports/reportTemplates/index.js';
import query from './data.graphql';
import style from './style.css';

const reportList = _get(window, 'cccisd.appDefs.reports.list', []);
const FAMILY_PROFILE_DEPLOYMENT_HANDLE = 'tfh_family_profile';
const initialTab = 'demographics';
export const TFH_SOCIOENVIRONMENTAL_DEPLOYMENT_HANDLE = 'assessment_parent_socio';

const FamilyProfile = ({ family, familyName, isStartOpen }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    // this is not repeatable, so deploymentId should stay constant
    const [socioenvironmentalDeploymentId, setSocioenvironmentalDeploymentId] = useState(null);
    const modalRef = useRef(null);

    const pawnId = _get(family, 'descendantRoles.metricspawn.pawn.pawnId');
    const pawnHash = _get(family, 'descendantRoles.metricspawn.pawn.pawnHash');

    useEffect(() => {
        async function fetchSocioenvironmentalDeploymentId() {
            let id = await getSocioenvironmentalDeploymentId();
            id = parseInt(id, 10);
            if (Number.isNaN(id) || id <= 0) {
                console.error('Could not find deployment with handle ' + TFH_SOCIOENVIRONMENTAL_DEPLOYMENT_HANDLE);
                return;
            }

            setSocioenvironmentalDeploymentId(id);
        }

        if (!socioenvironmentalDeploymentId) {
            fetchSocioenvironmentalDeploymentId();
        }
    }, [socioenvironmentalDeploymentId]);

    useEffect(() => {
        if (isStartOpen && modalRef.current) {
            modalRef.current.open();
        }
    }, [isStartOpen, modalRef]);

    async function getData() {
        setIsLoading(true);
        const resp = await client.query({
            query,
            variables: {
                pawnId,
                pawnHash,
                courseDeploymentHandle: TFH_DEPLOYMENT_HANDLE,
                profileDeploymentHandle: FAMILY_PROFILE_DEPLOYMENT_HANDLE,
            },
            fetchPolicy: 'network-only',
        });

        const respData = _get(resp, 'data.roles.metricspawn');
        setData(respData);
        setIsLoading(false);
    }

    function renderNoData() {
        return (
            <div className="alert alert-dark">
                <p className="mb-0">No data to show at this time</p>
            </div>
        );
    }

    const preferDescribe = 'Prefer to describe';
    function renderSex(key) {
        let selected = data.course.devTags[`demographics${key}Sex`];
        let userDefined = data.course.devTags[`demographics${key}SexOther`];
        if (userDefined && selected === preferDescribe) {
            return preferDescribe + ' (' + userDefined + ')';
        }

        if (selected) {
            return selected;
        }

        if (userDefined) {
            return userDefined;
        }

        return '-';
    }

    const other = 'Other';
    function renderRelation(key) {
        let selected = data.course.devTags[`demographics${key}Relation`];
        let userDefined = data.course.devTags[`demographics${key}RelationOther`];
        if (userDefined && selected === other) {
            return other + ' (' + userDefined + ')';
        }

        if (selected) {
            return selected;
        }

        if (userDefined) {
            return userDefined;
        }

        return '-';
    }

    function renderGrade(key) {
        let selected = data.course.devTags[`demographics${key}Education`];
        let userDefined = data.course.devTags[`demographics${key}EducationOther`];
        if (userDefined && selected === other) {
            return other + ' (' + userDefined + ')';
        }

        if (selected) {
            return selected;
        }

        if (userDefined) {
            return userDefined;
        }

        return '-';
    }

    function renderRace(key) {
        let combineFormula = data.course.devTags[`demographics${key}Race`];
        if (!combineFormula) {
            return '-';
        }

        const list = combineFormula
            .replace(/-/g, '')
            .split(',')
            .filter(z => z);
        if (!list.length) {
            return '-';
        }

        return list.join(', ');
    }

    function renderDemographicsRow(key) {
        if (!['Parent', 'Child1', 'Child2', 'Child3', 'Child4'].includes(key)) {
            return;
        }

        if (!data?.course?.devTags) {
            return key === 'Parent' ? renderNoData() : <span />;
        }

        const demographicsDevTags = data.course.devTags;

        // if no data at all, don't bother showing this row. It was probably skipped with visibility logic
        if (
            !Object.keys(demographicsDevTags).some(devTag => {
                return devTag.includes(key) && !!demographicsDevTags[devTag];
            })
        ) {
            return key === 'Parent' ? renderNoData() : <span />;
        }

        const now = new Date();
        const birthdate = demographicsDevTags[`demographics${key}Birthdate`];
        const formattedBirthdate =
            birthdate && typeof birthdate === 'string' && /^[\d]{4}-[\d]{2}-[\d]{2}$/.test(birthdate)
                ? `${birthdate.slice(5, 7)}/${birthdate.slice(8, 10)}/${birthdate.slice(0, 4)}`
                : birthdate;

        return (
            <div className={'row ' + style.demographicsRow}>
                <div className="col-12 col-sm-1">{key.replace(/[\d]/g, '')}</div>
                <div className="col-12 col-sm-11">
                    <dl className="row mb-0">
                        <dt className="col-4 my-0 py-0 text-end">Birthdate:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{formattedBirthdate || '-'}</dd>
                        <dt className="col-4 my-0 py-0 text-end">Current age:</dt>
                        <dd className="col-8 my-0 py-0 text-start">
                            {birthdate ? differenceInYears(now, new Date(birthdate)) + ' years' : '-'}
                        </dd>
                        <dt className="col-4 my-0 py-0 text-end">Sex at birth:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{renderSex(key)}</dd>
                        {key !== 'Parent' && (
                            <>
                                <dt className="col-4 my-0 py-0 text-end">Relationship to child:</dt>
                                <dd className="col-8 my-0 py-0 text-start">{renderRelation(key)}</dd>
                            </>
                        )}
                        <dt className="col-4 my-0 py-0 text-end">Hispanic:</dt>
                        <dd className="col-8 my-0 py-0 text-start">
                            {demographicsDevTags[`demographics${key}Hispanic`] || '-'}
                        </dd>
                        <dt className="col-4 my-0 py-0 text-end">Race:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{renderRace(key)}</dd>
                        <dt className="col-4 my-0 py-0 text-end">{key === 'Parent' ? 'Education' : 'Grade level'}:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{renderGrade(key)}</dd>
                        {key === 'Parent' && (
                            <>
                                <dt className="col-4 my-0 py-0 text-end">Work Status:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}WorkStatus`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Relationship Status:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}Relationship`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Household Members:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}HouseholdMemberCount`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Household Income:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}HouseholdIncome`] || '-'}
                                </dd>
                            </>
                        )}
                    </dl>
                </div>
            </div>
        );
    }

    return (
        <Modal
            ref={modalRef}
            title={'Family Profile - ' + familyName}
            size="large"
            beforeShow={() => getData()}
            trigger={
                <button type="button" className="btn btn-sm btn-secondary">
                    <IconFamily spaceRight />
                    Profile
                </button>
            }
            render={({ closeModal }) => {
                if (isLoading || !data) {
                    return (
                        <div className="py-5">
                            <Loader loading type="inline" />;
                        </div>
                    );
                }

                const close = (
                    <div className="mt-2">
                        <button className="btn btn-secondary" onClick={() => closeModal()} type="button">
                            Close
                        </button>
                    </div>
                );

                const tabList = [
                    {
                        name: 'demographics',
                        title: 'Demographics',
                        content: (
                            <div>
                                <div className={style.modalContent}>
                                    <div className="my-1">
                                        <h5>
                                            Demographics
                                            <Tooltip
                                                title={
                                                    <span>
                                                        Demographics information is entered by the family at the start
                                                        of the program. It is not editable.
                                                    </span>
                                                }
                                            >
                                                <span className="fs-6 text-primary">
                                                    <IconQuestion spaceLeft />
                                                </span>
                                            </Tooltip>
                                        </h5>
                                        {renderDemographicsRow('Parent')}
                                        {renderDemographicsRow('Child1')}
                                        {renderDemographicsRow('Child2')}
                                        {renderDemographicsRow('Child3')}
                                    </div>
                                </div>
                                {close}
                            </div>
                        ),
                    },
                    {
                        name: 'socioeconomic',
                        title: 'Socioenvironmental',
                        content: (
                            <div>
                                <div className={style.modalContent}>
                                    <div className="my-1">
                                        <h5 className="mb-4">
                                            Socioenvironmental Strengths and Areas for Improvement
                                            <Tooltip
                                                title={
                                                    <span>
                                                        Socioenvironmental information is entered by the family. To have
                                                        the family complete the assessment, copy the link from the
                                                        <b>Assessments</b> tab and share it with them.
                                                    </span>
                                                }
                                            >
                                                <span className="fs-6 text-primary">
                                                    <IconQuestion spaceLeft />
                                                </span>
                                            </Tooltip>
                                        </h5>
                                        {(() => {
                                            const report = reportList.find(
                                                item =>
                                                    item.id ===
                                                    'Socioenvironmental Strengths and Areas for Improvement Assessment'
                                            );
                                            const reportTemplate =
                                                report &&
                                                reportTemplates.find(
                                                    item => item.handle === report.reportTemplateHandle
                                                );

                                            return reportTemplate && socioenvironmentalDeploymentId ? (
                                                <reportTemplate.player
                                                    settings={report.reportTemplateSettings}
                                                    widgetProps={{
                                                        pawnId,
                                                        pawnHash,
                                                    }}
                                                    deploymentId={socioenvironmentalDeploymentId}
                                                    variables={{}}
                                                />
                                            ) : (
                                                <div className="alert alert-warning">
                                                    <p className="mb-0">
                                                        Report or Deployment not created for <b>Socioenvironmental</b>{' '}
                                                        assessment.
                                                    </p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {close}
                            </div>
                        ),
                    },
                    {
                        name: 'viewProfile',
                        title: 'View Profile',
                        content: (
                            <div>
                                <div className={style.modalContent}>
                                    <div className="my-1">
                                        <h5>
                                            Profile
                                            <Tooltip
                                                title={
                                                    isSupervisorViewing() ? (
                                                        <span>
                                                            The Together for Health Coach enters this data on behalf of
                                                            the family.
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            Profile information is editable by clicking the{' '}
                                                            <b>Update Profile</b> tab.
                                                        </span>
                                                    )
                                                }
                                            >
                                                <span className="fs-6 text-primary">
                                                    <IconQuestion spaceLeft />
                                                </span>
                                            </Tooltip>
                                        </h5>
                                        {!isSupervisorViewing() && (
                                            <p className="text-body-secondary fst-italic fw-light">
                                                Note: If this tab does not appear to be updating, you might need to
                                                click &quot;Save&quot; in the Update Profile tab.
                                            </p>
                                        )}
                                        <DeploymentPlayer
                                            pawnId={pawnId}
                                            pawnHash={pawnHash}
                                            disableLayout
                                            deploymentHandle={FAMILY_PROFILE_DEPLOYMENT_HANDLE}
                                            flowProps={{ isPrintMode: true }}
                                        />
                                    </div>
                                </div>
                                {close}
                            </div>
                        ),
                    },
                    ...(isSupervisorViewing()
                        ? []
                        : [
                              {
                                  name: 'updateProfile',
                                  title: 'Update Profile',
                                  content: (
                                      <div className={style.modalContent} data-cccisd-survey-area>
                                          <DeploymentPlayer
                                              pawnId={pawnId}
                                              pawnHash={pawnHash}
                                              disableLayout
                                              deploymentHandle={FAMILY_PROFILE_DEPLOYMENT_HANDLE}
                                          />
                                      </div>
                                  ),
                              },
                          ]),
                ];

                return <Tabs tabList={tabList} initialTab={initialTab} />;
            }}
        />
    );
};

export default FamilyProfile;
